import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import ThemeMaker from "../../components/thememaker";
import axios from "axios";
import { tokenDecode } from "../../utils/helper";
import { config } from "../../utils/config";
import moment from "moment";
import CircularProgress from "@mui/material/CircularProgress";
import { Modal as AntModal, Spin } from "antd";
import styles from "./returnorders.module.css";

const { apiEndpoint } = config;

const returnStatusOptions = [
  { value: "Return_Approved", label: "Return Approved", color: "#16A34A", bg: "#DCFCE7", border: "#BBF7D0", desc: "Approve the customer return request" },
  { value: "Return_Picked", label: "Return Picked", color: "#2563EB", bg: "#DBEAFE", border: "#BFDBFE", desc: "Item has been picked up from customer" },
  { value: "Return_Delivered", label: "Return Delivered", color: "#059669", bg: "#D1FAE5", border: "#A7F3D0", desc: "Returned item delivered to seller facility" },
  { value: "Liquidated", label: "Liquidated", color: "#7C3AED", bg: "#EDE9FE", border: "#DDD6FE", desc: "Item liquidated / disposed per seller policy" },
  { value: "Return_Rejected", label: "Return Rejected", color: "#DC2626", bg: "#FEE2E2", border: "#FECACA", desc: "Reject the customer return request" },
];

const ReturnOrders = () => {
  const decoded: any = tokenDecode();
  const [returnorderlist, setreturnorderlist]: any = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [orderpopup, setorderpopup] = useState(false);
  const [orderdetails, setorderdetails] = useState([]);
  const [status, setstatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Edit Return Status Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedReturnOrder, setSelectedReturnOrder] = useState<any>(null);
  const [selectedReturnStatus, setSelectedReturnStatus] = useState<string>("Return_Approved");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  const returnorder = (pageNumber = currentPage, size = pageSize, search = searchTerm, status = statusFilter) => {
    setIsLoading(true);
    axios
      .get(`${apiEndpoint}/snp/orders/getreturnOrder`, {
        params: {
          page: pageNumber,
          limit: size,
          pageSize: size,
          search: search || undefined,
          order_id: search || undefined,
          item_id: search || undefined,
          order_status: status || undefined,
        },
        headers: {
          "x-custom-authorization": `Bearer ${sessionStorage.getItem("token")}`,
        },
      })
      .then((response) => {
        console.log("getreturnOrder response", response.data);
        let result = response.data;
        let data = result?.orders || result?.data || [];
        if (result?.status === true || result?.status === 200) {
          setreturnorderlist(data);
          if (result?.pagination) {
            setCurrentPage(result.pagination.currentPage || pageNumber);
            setPageSize(result.pagination.pageSize || size);
            setTotalRecords(result.pagination.totalRecords ?? data.length);
            setTotalPages(result.pagination.totalPages || 1);
            setHasNextPage(Boolean(result.pagination.hasNextPage));
            setHasPreviousPage(Boolean(result.pagination.hasPreviousPage));
          } else {
            setCurrentPage(pageNumber);
            setTotalRecords(data.length);
            setTotalPages(Math.ceil(data.length / size) || 1);
          }
        } else {
          setreturnorderlist([]);
          setTotalRecords(0);
          setTotalPages(1);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log("error", error);
        setreturnorderlist([]);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    returnorder(currentPage, pageSize, searchTerm, statusFilter);
  }, [currentPage, pageSize, searchTerm, statusFilter]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (e: any) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const vieworderdetails = async (order_id: any, item_id: any) => {
    const rowdata: any = { order_id: order_id, product_id: item_id };
    setorderpopup(true);
    try {
      const response = await axios({
        method: "post",
        url: `${apiEndpoint}/getorderlist`,
        headers: { "x-custom-authorization": `Bearer ${sessionStorage.getItem("token")}` },
        data: rowdata,
      });
      setorderdetails(response.data.data);
    } catch (error) {
      console.error("error fetching order details", error);
    }
  };

  const handleOpenEditStatusModal = (orderItem: any) => {
    setSelectedReturnOrder(orderItem);
    const currentSt = orderItem?.order_status || orderItem?.status;
    const isValidOption = returnStatusOptions.some((opt) => opt.value === currentSt);
    setSelectedReturnStatus(isValidOption ? currentSt : "Return_Approved");
    setIsEditModalOpen(true);
  };

  const handleUpdateReturnStatus = async (e?: any) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!selectedReturnOrder?.order_id || !selectedReturnOrder?.item_id) {
      toast.error("Invalid Order ID or Item ID");
      return;
    }

    setIsUpdatingStatus(true);
    const payload = {
      order_id: String(selectedReturnOrder.order_id),
      item_id: String(selectedReturnOrder.item_id),
      order_status: selectedReturnStatus,
    };

    try {
      const response = await axios({
        method: "post",
        url: `${apiEndpoint}/unsolicated_on_update`,
        headers: {
          "Content-Type": "application/json",
          "x-custom-authorization": `Bearer ${sessionStorage.getItem("token")}`,
        },
        data: payload,
      });

      if (
        response?.data?.status === true ||
        response?.data?.status === 200 ||
        response?.data?.success ||
        response?.status === 200
      ) {
        toast.success(response?.data?.message || `Return status updated to ${selectedReturnStatus}!`);
        setIsEditModalOpen(false);
        returnorder(currentPage, pageSize);
      } else {
        toast.error(response?.data?.message || "Failed to update return status.");
      }
    } catch (error: any) {
      console.error("Error updating return status:", error);
      toast.error(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to update return status."
      );
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const orderStatusUpdate = async (orderId: any, itemId: any) => {
    let statusData = {
      order_id: String(orderId),
      item_id: String(itemId),
      order_status: status,
    };
    const id = toast.loading("Updating order status...");
    try {
      const response = await axios({
        method: "post",
        url: `${apiEndpoint}/unsolicated_on_update`,
        headers: {
          "Content-Type": "application/json",
          "x-custom-authorization": `Bearer ${sessionStorage.getItem("token")}`,
        },
        data: statusData,
      });
      if (response?.data?.status === true || response?.data?.status === 200 || response?.status === 200) {
        toast.update(id, {
          render: "Status updated successfully!",
          type: "success",
          isLoading: false,
          autoClose: 2500,
        });
        setorderpopup(false);
        returnorder(currentPage, pageSize);
      } else {
        toast.update(id, {
          render: response?.data?.message || "Failed to update status",
          type: "error",
          isLoading: false,
          autoClose: 2500,
        });
      }
    } catch (error: any) {
      console.log("error updating status", error);
      toast.update(id, {
        render: error?.response?.data?.message || "Something went wrong",
        type: "error",
        isLoading: false,
        autoClose: 2500,
      });
    }
  };

  const changeReturnStatus = (item: any, selectedStatus: any) => {
    item.status = selectedStatus;
    setstatus(selectedStatus);
  };

  const copyToClipboard = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("Item ID copied to clipboard!");
  };

  const renderItemId = (itemId: string) => {
    if (!itemId) return "-";
    const shortId = itemId.length > 12 ? `${itemId.substring(0, 12)}...` : itemId;
    return (
      <div
        className="d-inline-flex align-items-center"
        style={{
          backgroundColor: "#F8FAFC",
          padding: "4px 8px",
          borderRadius: "6px",
          border: "1px solid #E2E8F0",
          fontFamily: "monospace",
          fontSize: "12px",
          color: "#475569",
          whiteSpace: "nowrap",
          gap: "6px",
          cursor: "pointer",
        }}
        title={`Click to copy: ${itemId}`}
        onClick={() => copyToClipboard(itemId)}
      >
        <span>{shortId}</span>
        <i
          className="fas fa-copy"
          style={{
            fontSize: "12px",
            color: "#F97316",
            cursor: "pointer",
          }}
        ></i>
      </div>
    );
  };

  const renderStatusBadge = (order_status: string) => {
    const st = (order_status || "Return_Initiated").trim();
    let bg = "#EEF2FF";
    let color = "#4F46E5";
    let border = "1px solid #C7D2FE";

    if (st === "Return_Initiated") {
      bg = "#FEF3C7";
      color = "#D97706";
      border = "1px solid #FDE68A";
    } else if (st === "Return_Approved") {
      bg = "#DCFCE7";
      color = "#15803D";
      border = "1px solid #BBF7D0";
    } else if (st === "Return_Rejected") {
      bg = "#FEE2E2";
      color = "#B91C1C";
      border = "1px solid #FECACA";
    } else if (st === "Return_Picked" || st === "Return_Delivered") {
      bg = "#E0E7FF";
      color = "#4338CA";
      border = "1px solid #C7D2FE";
    } else if (st === "Liquidated" || st === "Cancelled") {
      bg = "#F1F5F9";
      color = "#475569";
      border = "1px solid #E2E8F0";
    }

    return (
      <span
        style={{
          backgroundColor: bg,
          color: color,
          border: border,
          padding: "4px 12px",
          borderRadius: "20px",
          fontSize: "12px",
          fontWeight: 700,
          display: "inline-block",
          letterSpacing: "0.4px",
          whiteSpace: "nowrap",
        }}
      >
        {st.replace(/_/g, " ")}
      </span>
    );
  };

  const filteredOrders = (returnorderlist || []).filter((item: any) => {
    const matchesSearch =
      !searchTerm ||
      item?.order_id?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      item?.item_id?.toString().toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      !statusFilter ||
      item?.order_status?.toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  return (
    <>
      <style>{`
        .return-orders-card {
          border-radius: 20px !important;
          border: 1px solid #E5E7EB !important;
          background-color: #ffffff !important;
          box-shadow: 0 10px 30px rgba(15, 23, 42, 0.04) !important;
        }

        .return-table-wrapper {
          border-radius: 14px;
          overflow: hidden;
          border: 1px solid #E2E8F0;
        }

        .return-custom-table {
          width: 100%;
          border-collapse: separate !important;
          border-spacing: 0 !important;
        }

        .return-custom-table thead tr {
          background-color: #F97316 !important;
        }

        .return-custom-table th {
          background-color: #F97316 !important;
          color: #FFFFFF !important;
          font-weight: 700 !important;
          font-size: 13px !important;
          padding: 10px 14px !important;
          white-space: nowrap !important;
          border: none !important;
          letter-spacing: 0.3px;
        }

        .return-custom-table td {
          padding: 10px 14px !important;
          border-bottom: 1px solid #F1F5F9 !important;
          vertical-align: middle !important;
          font-size: 13px !important;
          color: #334155 !important;
        }

        .return-custom-table tbody tr:nth-child(even) {
          background-color: #FFFBF7 !important;
        }

        .return-custom-table tbody tr:nth-child(odd) {
          background-color: #FFFFFF !important;
        }

        .return-custom-table tbody tr:hover {
          background-color: #FFF2E8 !important;
        }

        .return-mobile-card {
          background: #ffffff;
          border: 1px solid #E2E8F0;
          border-radius: 16px;
          padding: 16px;
          margin-bottom: 16px;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.03);
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .return-mobile-card:hover {
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.07);
        }

        .action-btn-view {
          background-color: #F97316;
          color: #FFFFFF;
          border: none;
          padding: 5px 14px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(249, 115, 22, 0.2);
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
          text-decoration: none !important;
        }

        .action-btn-view:hover {
          background-color: #EA580C;
          color: #FFFFFF;
        }

        .action-btn-update {
          background-color: #FFFFFF;
          color: #0F172A;
          border: 1.5px solid #CBD5E1;
          padding: 5px 14px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.2s;
        }

        .action-btn-update:hover {
          background-color: #F1F5F9;
          border-color: #94A3B8;
        }
      `}</style>

      <ThemeMaker>
        <div className="row w-100 m-0 mt-2">
          <div className="col-md-12">
            {/* Sticky Header Card */}
            <div className="card return-orders-card p-4 w-100 mb-3" style={{ position: "sticky", top: "10px", zIndex: 30, borderRadius: "20px", borderColor: "#E5E7EB", backgroundColor: "#ffffff", boxShadow: "0 10px 30px rgba(15, 23, 42, 0.04)" }}>
              {/* Header Title & Controls Row */}
              <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: "15px" }}>
                <div className="d-flex align-items-center" style={{ gap: "12px" }}>
                  <h4 className="font-weight-bold m-0" style={{ color: "#0F172A", fontSize: "24px" }}>
                    Return Orders
                  </h4>
                  <span
                    style={{
                      backgroundColor: "#FFF2E8",
                      color: "#F97316",
                      fontSize: "13px",
                      fontWeight: 700,
                      padding: "4px 12px",
                      borderRadius: "20px",
                      border: "1px solid #FFEDD5",
                    }}
                  >
                    {filteredOrders.length} {filteredOrders.length === 1 ? "Order" : "Orders"}
                  </span>
                </div>

                <div className="d-flex align-items-center flex-wrap" style={{ gap: "12px" }}>
                  {/* Search Order ID / Item ID Fieldset */}
                  <fieldset
                    style={{
                      border: "1.5px solid #F97316",
                      borderRadius: "10px",
                      padding: "0 12px 6px 12px",
                      margin: 0,
                      height: "46px",
                      backgroundColor: "#FFFFFF",
                    }}
                  >
                    <legend
                      style={{
                        width: "auto",
                        padding: "0 6px",
                        fontSize: "11px",
                        color: "#F97316",
                        fontWeight: 600,
                        margin: 0,
                      }}
                    >
                      Search Order / Item ID
                    </legend>
                    <div className="d-flex align-items-center" style={{ marginTop: "-6px" }}>
                      <i className="fas fa-search mr-2" style={{ fontSize: "13px", color: "#F97316" }}></i>
                      <input
                        type="search"
                        placeholder="Search ID..."
                        className="border-0 bg-transparent text-dark"
                        onChange={(e) => {
                          setSearchTerm(e.target.value.trim());
                          setCurrentPage(1);
                        }}
                        value={searchTerm}
                        style={{ outline: "none", fontSize: "14px", width: "170px" }}
                      />
                    </div>
                  </fieldset>

                  {/* Return Status Filter Fieldset */}
                  <fieldset
                    style={{
                      border: "1.5px solid #F97316",
                      borderRadius: "10px",
                      padding: "0 12px 6px 12px",
                      margin: 0,
                      height: "46px",
                      backgroundColor: "#FFFFFF",
                    }}
                  >
                    <legend
                      style={{
                        width: "auto",
                        padding: "0 6px",
                        fontSize: "11px",
                        color: "#F97316",
                        fontWeight: 600,
                        margin: 0,
                      }}
                    >
                      Order Status
                    </legend>
                    <div className="d-flex align-items-center" style={{ marginTop: "-6px" }}>
                      <select
                        onChange={(e) => {
                          setStatusFilter(e.target.value);
                          setCurrentPage(1);
                        }}
                        value={statusFilter}
                        style={{
                          border: "none",
                          outline: "none",
                          width: "150px",
                          height: "26px",
                          background: "transparent",
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#334155",
                          cursor: "pointer",
                        }}
                      >
                        <option value="">All Status</option>
                        <option value="Return_Initiated">Return Initiated</option>
                        <option value="Return_Approved">Return Approved</option>
                        <option value="Return_Rejected">Return Rejected</option>
                        <option value="Return_Picked">Return Picked</option>
                        <option value="Return_Delivered">Return Delivered</option>
                        <option value="Liquidated">Liquidated</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </div>
                  </fieldset>

                  {/* Refresh Button */}

                </div>
              </div>
            </div>

            {/* Table Container Card */}
            <div className="card shadow-sm border p-0 w-100 mb-4" style={{ borderRadius: "16px", borderColor: "#E5E7EB", backgroundColor: "#ffffff", boxShadow: "0 10px 30px rgba(15, 23, 42, 0.04)", overflow: "hidden" }}>
              {/* Main Content Area */}
              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center py-5" style={{ minHeight: "300px" }}>
                  <CircularProgress style={{ color: "#F97316" }} />
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="text-center py-5" style={{ color: "#64748B" }}>
                  <i className="fas fa-box-open mb-3" style={{ fontSize: "48px", color: "#CBD5E1" }}></i>
                  <h5 style={{ fontWeight: 600, color: "#334155" }}>No Return Orders Found</h5>
                  <p style={{ fontSize: "14px" }}>There are no return orders matching your filters.</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="d-none d-md-block return-table-wrapper" style={{ border: "none" }}>
                    <div className="table-responsive" style={{ overflowY: "auto", maxHeight: "calc(100vh - 280px)", minHeight: "350px" }}>
                      <table className="return-custom-table mb-0">
                        <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                          <tr>
                            <th style={{ backgroundColor: "#F97316" }}>S.No</th>
                            <th style={{ backgroundColor: "#F97316" }}>Order ID</th>
                            <th style={{ backgroundColor: "#F97316" }}>Item ID</th>
                            <th style={{ backgroundColor: "#F97316" }}>Action</th>
                            <th style={{ backgroundColor: "#F97316" }}>Return Price</th>
                            <th style={{ backgroundColor: "#F97316" }}>Qty</th>
                            <th style={{ backgroundColor: "#F97316" }}>Fulfillment</th>
                            <th style={{ backgroundColor: "#F97316" }}>Status</th>
                            <th style={{ backgroundColor: "#F97316" }}>Created Date</th>
                            <th style={{ textAlign: "center", backgroundColor: "#F97316" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredOrders.map((item: any, index: number) => {
                            return (
                              <tr key={item?.id || index}>
                                <td style={{ fontWeight: 600, color: "#64748B" }}>
                                  {(currentPage - 1) * pageSize + index + 1}
                                </td>
                                <td style={{ fontWeight: 700, color: "#0F172A", fontSize: "14px" }}>
                                  {item?.order_id || "-"}
                                </td>
                                <td>{renderItemId(item?.item_id)}</td>
                                <td style={{ fontWeight: 600, color: "#334155" }}>{item?.action || "Return"}</td>
                                <td style={{ fontWeight: 700, color: "#F97316", fontSize: "15px" }}>
                                  ₹{item?.return_price || "0.00"}
                                </td>
                                <td style={{ fontWeight: 600, color: "#475569" }}>
                                  {item?.return_qty || item?.cancel_qty || 1}
                                </td>
                                <td style={{ color: "#64748B", fontSize: "13px" }}>
                                  {item?.fulfillment_type || "-"}
                                </td>
                                <td>{renderStatusBadge(item?.order_status)}</td>
                                <td style={{ color: "#475569", fontSize: "13px" }}>
                                  {item?.created_at
                                    ? moment(item.created_at).format("DD MMM YYYY, hh:mm A")
                                    : "-"}
                                </td>
                                <td>
                                  <div className="d-flex justify-content-center align-items-center" style={{ gap: "8px" }}>
                                    <Link
                                      to={`/orderCompleteDetail/${item?.order_id}`}
                                      className="action-btn-view"
                                      title="View Order Details"
                                    >
                                      <i className="fas fa-eye" style={{ fontSize: "12px" }}></i> View
                                    </Link>
                                    <button
                                      type="button"
                                      className="btn btn-sm d-inline-flex align-items-center justify-content-center"
                                      style={{
                                        backgroundColor: "#FFF7ED",
                                        color: "#F97316",
                                        border: "1.5px solid #F97316",
                                        borderRadius: "8px",
                                        fontSize: "12.5px",
                                        fontWeight: 700,
                                        padding: "4px 12px",
                                        gap: "5px",
                                        cursor: "pointer",
                                        boxShadow: "0 2px 4px rgba(249, 115, 22, 0.1)",
                                        transition: "all 0.2s"
                                      }}
                                      onClick={() => handleOpenEditStatusModal(item)}
                                      title="Edit / Update Return Status"
                                    >
                                      <i className="fas fa-edit" style={{ fontSize: "12px" }}></i> Edit
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile Listing View */}
                  <div className="d-block d-md-none">
                    {filteredOrders.map((item: any, index: number) => {
                      return (
                        <div className="return-mobile-card" key={item?.id || index}>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                              <span
                                style={{
                                  backgroundColor: "#FFF2E8",
                                  color: "#F97316",
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  padding: "2px 8px",
                                  borderRadius: "12px",
                                }}
                              >
                                #{(currentPage - 1) * pageSize + index + 1}
                              </span>
                              <span style={{ fontWeight: 700, color: "#0F172A", fontSize: "16px" }}>
                                Order #{item?.order_id}
                              </span>
                            </div>
                            <span style={{ fontWeight: 700, color: "#F97316", fontSize: "16px" }}>
                              ₹{item?.return_price}
                            </span>
                          </div>

                          <div className="mb-3" style={{ fontSize: "13px", color: "#475569" }}>
                            <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                              <span style={{ color: "#94A3B8" }}>Item ID:</span>
                              {renderItemId(item?.item_id)}
                            </div>
                            <div className="d-flex justify-content-between py-1 border-bottom">
                              <span style={{ color: "#94A3B8" }}>Return Qty:</span>
                              <span style={{ fontWeight: 600 }}>{item?.return_qty || item?.cancel_qty || 1}</span>
                            </div>
                            <div className="d-flex justify-content-between py-1 border-bottom">
                              <span style={{ color: "#94A3B8" }}>Action:</span>
                              <span style={{ fontWeight: 600 }}>{item?.action || "Return"}</span>
                            </div>
                            <div className="d-flex justify-content-between py-1 border-bottom">
                              <span style={{ color: "#94A3B8" }}>Created Date:</span>
                              <span>
                                {item?.created_at ? moment(item.created_at).format("DD MMM YYYY, hh:mm A") : "-"}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center pt-2">
                              <span style={{ color: "#94A3B8" }}>Status:</span>
                              <div>{renderStatusBadge(item?.order_status)}</div>
                            </div>
                          </div>

                          <div className="d-flex justify-content-end pt-2" style={{ gap: "10px" }}>
                            <Link to={`/orderCompleteDetail/${item?.order_id}`} className="action-btn-view">
                              <i className="fas fa-eye" style={{ fontSize: "12px" }}></i> View
                            </Link>
                            <button
                              type="button"
                              className="btn btn-sm d-inline-flex align-items-center"
                              style={{
                                backgroundColor: "#FFF7ED",
                                color: "#F97316",
                                border: "1.5px solid #F97316",
                                borderRadius: "8px",
                                fontSize: "12.5px",
                                fontWeight: 700,
                                padding: "4px 12px",
                                gap: "6px"
                              }}
                              onClick={() => handleOpenEditStatusModal(item)}
                            >
                              <i className="fas fa-edit" style={{ fontSize: "12px" }}></i> Edit
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination Footer */}
                  {totalRecords > 0 && (
                    <div
                      className="d-flex flex-wrap justify-content-between align-items-center p-3 border-top"
                      style={{ backgroundColor: "#FFFFFF", gap: "12px" }}
                    >
                      {/* Entries summary & Page size selector */}
                      <div className="d-flex align-items-center flex-wrap" style={{ gap: "12px", fontSize: "13px", color: "#64748B" }}>
                        <span>
                          Showing <strong>{Math.min((currentPage - 1) * pageSize + 1, totalRecords)}</strong> to{" "}
                          <strong>{Math.min(currentPage * pageSize, totalRecords)}</strong> of <strong>{totalRecords}</strong> entries
                        </span>
                        <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                          <span>Rows per page:</span>
                          <select
                            value={pageSize}
                            onChange={handlePageSizeChange}
                            style={{
                              width: "70px",
                              height: "32px",
                              padding: "2px 8px",
                              fontSize: "13px",
                              borderRadius: "6px",
                              borderColor: "#CBD5E1",
                              cursor: "pointer",
                              background: "#F8FAFC",
                              color: "#334155",
                              fontWeight: 600,
                            }}
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>

                      {/* Pagination Controls */}
                      {totalPages > 1 && (
                        <ul className="pagination mb-0 flex-wrap" style={{ gap: "4px" }}>
                          <li className={`page-item ${!hasPreviousPage && currentPage === 1 ? "disabled" : ""}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(1)}
                              disabled={!hasPreviousPage && currentPage === 1}
                              style={{
                                borderRadius: "6px",
                                padding: "6px 12px",
                                fontSize: "13px",
                                fontWeight: 600,
                                color: (!hasPreviousPage && currentPage === 1) ? "#94A3B8" : "#334155",
                                backgroundColor: (!hasPreviousPage && currentPage === 1) ? "#F8FAFC" : "#FFFFFF",
                                border: "1px solid #E2E8F0",
                                cursor: (!hasPreviousPage && currentPage === 1) ? "not-allowed" : "pointer",
                              }}
                            >
                              First
                            </button>
                          </li>
                          <li className={`page-item ${!hasPreviousPage && currentPage === 1 ? "disabled" : ""}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={!hasPreviousPage && currentPage === 1}
                              style={{
                                borderRadius: "6px",
                                padding: "6px 12px",
                                fontSize: "13px",
                                fontWeight: 600,
                                color: (!hasPreviousPage && currentPage === 1) ? "#94A3B8" : "#334155",
                                backgroundColor: (!hasPreviousPage && currentPage === 1) ? "#F8FAFC" : "#FFFFFF",
                                border: "1px solid #E2E8F0",
                                cursor: (!hasPreviousPage && currentPage === 1) ? "not-allowed" : "pointer",
                              }}
                            >
                              Prev
                            </button>
                          </li>

                          {(() => {
                            const pages = [];
                            const current = currentPage;
                            if (current > 3) {
                              pages.push(
                                <li key="page-1" className="page-item">
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(1)}
                                    style={{
                                      borderRadius: "6px",
                                      padding: "6px 12px",
                                      fontSize: "13px",
                                      fontWeight: 600,
                                      color: "#334155",
                                      border: "1px solid #E2E8F0",
                                    }}
                                  >
                                    1
                                  </button>
                                </li>
                              );
                              if (current > 4) {
                                pages.push(
                                  <li key="ellipsis-1" className="page-item disabled">
                                    <span className="page-link" style={{ border: "none", background: "transparent", color: "#94A3B8" }}>
                                      ...
                                    </span>
                                  </li>
                                );
                              }
                            }

                            const startPage = Math.max(1, current - 2);
                            const endPage = Math.min(totalPages, current + 2);
                            for (let i = startPage; i <= endPage; i++) {
                              const isActive = i === current;
                              pages.push(
                                <li key={`page-${i}`} className={`page-item ${isActive ? "active" : ""}`}>
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(i)}
                                    style={{
                                      borderRadius: "6px",
                                      padding: "6px 12px",
                                      fontSize: "13px",
                                      fontWeight: 700,
                                      backgroundColor: isActive ? "#F97316" : "#FFFFFF",
                                      borderColor: isActive ? "#F97316" : "#E2E8F0",
                                      color: isActive ? "#FFFFFF" : "#334155",
                                    }}
                                  >
                                    {i}
                                  </button>
                                </li>
                              );
                            }

                            if (current < totalPages - 2) {
                              if (current < totalPages - 3) {
                                pages.push(
                                  <li key="ellipsis-2" className="page-item disabled">
                                    <span className="page-link" style={{ border: "none", background: "transparent", color: "#94A3B8" }}>
                                      ...
                                    </span>
                                  </li>
                                );
                              }
                              pages.push(
                                <li key={`page-${totalPages}`} className="page-item">
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(totalPages)}
                                    style={{
                                      borderRadius: "6px",
                                      padding: "6px 12px",
                                      fontSize: "13px",
                                      fontWeight: 600,
                                      color: "#334155",
                                      border: "1px solid #E2E8F0",
                                    }}
                                  >
                                    {totalPages}
                                  </button>
                                </li>
                              );
                            }
                            return pages;
                          })()}

                          <li className={`page-item ${!hasNextPage && currentPage === totalPages ? "disabled" : ""}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={!hasNextPage && currentPage === totalPages}
                              style={{
                                borderRadius: "6px",
                                padding: "6px 12px",
                                fontSize: "13px",
                                fontWeight: 600,
                                color: (!hasNextPage && currentPage === totalPages) ? "#94A3B8" : "#334155",
                                backgroundColor: (!hasNextPage && currentPage === totalPages) ? "#F8FAFC" : "#FFFFFF",
                                border: "1px solid #E2E8F0",
                                cursor: (!hasNextPage && currentPage === totalPages) ? "not-allowed" : "pointer",
                              }}
                            >
                              Next
                            </button>
                          </li>
                          <li className={`page-item ${!hasNextPage && currentPage === totalPages ? "disabled" : ""}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(totalPages)}
                              disabled={!hasNextPage && currentPage === totalPages}
                              style={{
                                borderRadius: "6px",
                                padding: "6px 12px",
                                fontSize: "13px",
                                fontWeight: 600,
                                color: (!hasNextPage && currentPage === totalPages) ? "#94A3B8" : "#334155",
                                backgroundColor: (!hasNextPage && currentPage === totalPages) ? "#F8FAFC" : "#FFFFFF",
                                border: "1px solid #E2E8F0",
                                cursor: (!hasNextPage && currentPage === totalPages) ? "not-allowed" : "pointer",
                              }}
                            >
                              Last
                            </button>
                          </li>
                        </ul>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Edit Return Order Status Modal */}
        <AntModal
          open={isEditModalOpen}
          onCancel={() => !isUpdatingStatus && setIsEditModalOpen(false)}
          footer={null}
          width={560}
          centered
          zIndex={1060}
          title={
            <div className="d-flex align-items-center justify-content-between pr-3 py-1">
              <div className="d-flex align-items-center flex-wrap" style={{ gap: "10px" }}>
                <span style={{ fontSize: "17px", fontWeight: 700, color: "#0F172A" }}>
                  Update Return Status
                </span>
                {selectedReturnOrder?.order_id && (
                  <span
                    style={{
                      fontSize: "12.5px",
                      fontWeight: 700,
                      color: "#F97316",
                      backgroundColor: "#FFF7ED",
                      border: "1px solid #FFEDD5",
                      padding: "2px 10px",
                      borderRadius: "12px",
                    }}
                  >
                    Order #{selectedReturnOrder.order_id}
                  </span>
                )}
              </div>
            </div>
          }
        >
          <form onSubmit={handleUpdateReturnStatus} className="pt-2">
            {/* Order & Item Summary Card */}
            <div
              className="p-3 mb-3"
              style={{
                backgroundColor: "#F8FAFC",
                borderRadius: "12px",
                border: "1px solid #E2E8F0",
              }}
            >
              <div className="row g-2" style={{ fontSize: "13px" }}>
                <div className="col-6 mb-2">
                  <span className="text-muted d-block" style={{ fontSize: "11.5px" }}>Order ID</span>
                  <strong style={{ color: "#0F172A" }}>{selectedReturnOrder?.order_id || "-"}</strong>
                </div>
                <div className="col-6 mb-2">
                  <span className="text-muted d-block" style={{ fontSize: "11.5px" }}>Current Status</span>
                  <div className="mt-1">{renderStatusBadge(selectedReturnOrder?.order_status || selectedReturnOrder?.status)}</div>
                </div>
                <div className="col-12 mb-2">
                  <span className="text-muted d-block" style={{ fontSize: "11.5px" }}>Item ID</span>
                  <div className="mt-1">{renderItemId(selectedReturnOrder?.item_id)}</div>
                </div>
                <div className="col-6">
                  <span className="text-muted d-block" style={{ fontSize: "11.5px" }}>Return Price</span>
                  <strong style={{ color: "#F97316" }}>₹{selectedReturnOrder?.return_price || "0.00"}</strong>
                </div>
                <div className="col-6">
                  <span className="text-muted d-block" style={{ fontSize: "11.5px" }}>Quantity</span>
                  <strong style={{ color: "#334155" }}>{selectedReturnOrder?.return_qty || selectedReturnOrder?.cancel_qty || 1}</strong>
                </div>
              </div>
            </div>

            {/* Select Status Options */}
            <div className="mb-3">
              <label
                className="form-label font-weight-bold d-block mb-2"
                style={{ fontSize: "13px", color: "#1E293B" }}
              >
                Select New Return Status <span className="text-danger">*</span>
              </label>

              <div className="d-flex flex-column" style={{ gap: "8px" }}>
                {returnStatusOptions.map((opt) => {
                  const isSelected = selectedReturnStatus === opt.value;
                  return (
                    <div
                      key={opt.value}
                      onClick={() => setSelectedReturnStatus(opt.value)}
                      style={{
                        padding: "10px 14px",
                        borderRadius: "10px",
                        border: isSelected ? `2px solid ${opt.color}` : "1px solid #E2E8F0",
                        backgroundColor: isSelected ? opt.bg : "#FFFFFF",
                        cursor: "pointer",
                        transition: "all 0.18s ease-in-out",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        boxShadow: isSelected ? "0 2px 8px rgba(0, 0, 0, 0.04)" : "none",
                      }}
                    >
                      <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                        <input
                          type="radio"
                          name="return_status_option"
                          checked={isSelected}
                          onChange={() => setSelectedReturnStatus(opt.value)}
                          style={{ accentColor: opt.color, cursor: "pointer", width: "16px", height: "16px" }}
                        />
                        <div>
                          <div style={{ fontWeight: 700, fontSize: "13.5px", color: isSelected ? opt.color : "#1E293B" }}>
                            {opt.label}
                          </div>
                          <div style={{ fontSize: "11.5px", color: "#64748B" }}>
                            {opt.desc}
                          </div>
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          padding: "2px 8px",
                          borderRadius: "6px",
                          backgroundColor: opt.bg,
                          color: opt.color,
                          border: `1px solid ${opt.border}`,
                        }}
                      >
                        {opt.value}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Modal Actions Footer */}
            <div className="d-flex justify-content-end align-items-center mt-4 pt-3 border-top" style={{ gap: "10px" }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm px-4"
                style={{ borderRadius: "8px", fontWeight: 600, height: "38px" }}
                onClick={() => setIsEditModalOpen(false)}
                disabled={isUpdatingStatus}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn text-white font-weight-bold d-flex align-items-center justify-content-center shadow-sm"
                style={{
                  backgroundColor: "#F97316",
                  borderColor: "#F97316",
                  borderRadius: "8px",
                  fontWeight: 700,
                  fontSize: "13.5px",
                  height: "38px",
                  minWidth: "160px",
                  gap: "8px",
                }}
                disabled={isUpdatingStatus}
              >
                {isUpdatingStatus ? (
                  <>
                    <Spin size="small" />
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-check-circle"></i>
                    <span>Update Status</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </AntModal>
      </ThemeMaker>
    </>
  );
};

export default ReturnOrders;
